import { cropRings, type ListingCrop } from './listingCropsRun';

// A PHOTOGRAPH OF THE PRODUCT WITH THE PART DRAWN ON IT.
//
// WHY. The orientation call asks a model to match six views of a part against photographs of
// the whole chair, which is two jobs: find the part in the picture, then work out which of
// its sides is facing the camera. The first one is already answered — Phase 1 found the part
// in these very photographs and somebody has had the chance to strike the wrong ones through
// — and answering it again from scratch is where a part comes back upside down.
//
// Drawn ON the photograph rather than cut OUT of it. The context is half the value: a
// rectangle of a side rail says what the rail looks like and not that the seat is above it
// and the floor below, which is exactly the fact the top/bottom half of the answer turns on.
//
// WHAT IS DRAWN IS WHAT IS SENT. A crop somebody turned off is not drawn here and does not
// reach the model — the toggle in the prompt's own zoom is the same toggle the parts table
// has, writing the same flag through the same store. See MeshControls, which filters, and
// toggleListingCrop, which is where the flag lives.

// The green the app already outlines a found part in — blue-500, the same colour the
// tiles in the parts table draw and the same one a person will have been looking at while
// deciding whether the box is right. A second green here would be a second claim.
const INK = '#10b981';
const FILL = 'rgba(16, 185, 129, 0.28)';

// How big a photograph may go up. A listing picture is often 2000 px and none of this needs
// that: the part is being recognised, not measured, and every pixel past a point is payload.
const MAX_SIDE = 1400;

// One photograph with its part outlined, as a data URL — or the picture untouched when
// there is nothing to draw on it, which is most parts on most photographs.
export async function paintMaskedShot(src: string, crops: ListingCrop[]): Promise<string> {
  const img = await load(src);
  const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.round(img.naturalWidth * scale);
  const h = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);

  for (const crop of crops) {
    const rings = cropRings(crop);
    // THE OUTLINE IF THERE IS ONE, THE BOX IF NOT. Almost no part is a rectangle — a rear
    // leg frame is a diagonal through its own box and three quarters of that box is the wall
    // behind it — so the polygon is the honest shape and the rectangle is the fallback for a
    // crop stored before there were any.
    const shapes = rings.length
      ? rings.map((ring) => ring.map(([x, y]) => [x * w, y * h] as const))
      : [rect(crop.bbox, w, h)];
    ctx.lineWidth = Math.max(2, Math.round(Math.min(w, h) * 0.004));
    ctx.strokeStyle = INK;
    ctx.fillStyle = FILL;
    for (const shape of shapes) {
      if (shape.length < 2) continue;
      ctx.beginPath();
      shape.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.closePath();
      // Filled AND outlined. The fill is what makes a part legible at a glance among the
      // several the picture holds; the line is what says where it ends, which a translucent
      // wash over wood does not.
      ctx.fill();
      ctx.stroke();
    }
  }
  // JPEG, and not the PNG the renders use. These are photographs — a lossless one is four
  // times the bytes for detail nobody is reading — and four of them go up with every press.
  return canvas.toDataURL('image/jpeg', 0.9);
}

const rect = (
  [x0, y0, x1, y1]: [number, number, number, number], w: number, h: number,
) => [
  [x0 * w, y0 * h], [x1 * w, y0 * h], [x1 * w, y1 * h], [x0 * w, y1 * h],
] as const as readonly (readonly [number, number])[];

function load(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Same origin — these come from /api/notice/listing-image — so the canvas stays clean
    // and toDataURL works. A merchant's CDN copy would taint it and throw on read.
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}
